// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/ProjectsList.tsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { Project } from './App';

interface ProjectsListProps {
  projects: Project[];
  onNavigateToNewProject: () => void;
  onDeleteProject: (projectId: number) => void;
  onModifyProject: (project: Project) => void;
  onSelectProject: (projectId: number) => void;
}

const ProjectsList: React.FC<ProjectsListProps> = ({
  projects,
  onNavigateToNewProject,
  onDeleteProject,
  onModifyProject,
  onSelectProject,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openModifyDialog, setOpenModifyDialog] = useState(false);
  const [currentProjectToModify, setCurrentProjectToModify] = useState<Project | null>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, projectId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedProjectId(projectId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProjectId(null);
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleModifyClick = () => {
    const project = projects.find(p => p.id === selectedProjectId);
    if (project) {
      setCurrentProjectToModify({ ...project }); // Create a copy to avoid direct state mutation
      setOpenModifyDialog(true);
    }
    handleMenuClose();
  };

  const confirmDelete = () => {
    if (selectedProjectId) {
      onDeleteProject(selectedProjectId);
      setOpenDeleteDialog(false);
      setSelectedProjectId(null);
    }
  };

  const handleModifyDialogClose = () => {
    setOpenModifyDialog(false);
    setCurrentProjectToModify(null);
  };

  const handleModifyDialogSave = () => {
    if (currentProjectToModify) {
      onModifyProject(currentProjectToModify);
      handleModifyDialogClose();
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (currentProjectToModify) {
      setCurrentProjectToModify(prev => {
        if (!prev) return null;
        return { ...prev, [name]: value };
      });
    }
  };

  if (projects.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 3, mt: 3 }}>
        <DescriptionIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
        <Typography variant="h5" gutterBottom>There are no existing projects. Let's get started!</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mb: 4 }}>Create your first project to begin optimizing your workflows and managing your tasks efficiently.</Typography>
        <Button variant="contained" size="large" startIcon={<AddCircleOutlineIcon />} onClick={onNavigateToNewProject}>Start Here</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>Your Projects</Typography>
        <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={onNavigateToNewProject}>Create a Project</Button>
      </Box>

      <Grid container spacing={3}>
        {projects.map((project) => (
          <Grid item xs={12} sm={6} md={4} key={project.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" gutterBottom onClick={() => onSelectProject(project.id)} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>{project.name}</Typography>
                  <IconButton aria-label="settings" onClick={(event) => handleMenuClick(event, project.id)} size="small"><MoreVertIcon /></IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl) && selectedProjectId === project.id} onClose={handleMenuClose}>
                    <MenuItem onClick={handleModifyClick}><EditIcon sx={{ mr: 1 }} /> Modify</MenuItem>
                    <MenuItem onClick={handleDeleteClick}><DeleteIcon sx={{ mr: 1 }} /> Delete</MenuItem>
                  </Menu>
                </Box>
                <Typography variant="body2" color="text.secondary">Location: {project.location}</Typography>
                <Typography variant="body2" color="text.secondary">Created By: {project.createdBy}</Typography>
                <Typography variant="body2" color="text.secondary">Last Viewed: {new Date(project.dateViewed).toLocaleDateString()}</Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Plan Summary:</Typography>
                  <List dense disablePadding>
                    {project.planComponents && project.planComponents.length > 0 ? (
                      project.planComponents.slice(0, 3).map((component) => (
                        <ListItem key={component.id} sx={{ py: 0.25, pl: 1 }}>
                          <ListItemText
                            primary={component.name}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem sx={{ py: 0.25, pl: 1 }}>
                        <ListItemText secondary="No plan components available." sx={{ fontStyle: 'italic' }} />
                      </ListItem>
                    )}
                    {project.planComponents && project.planComponents.length > 3 && (
                        <ListItem sx={{ py: 0.25, pl: 1 }}>
                            <ListItemText
                                secondary={`+${project.planComponents.length - 3} more...`}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                    )}
                  </List>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <Button size="small" onClick={() => onSelectProject(project.id)}>View Details</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete this project?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button><Button onClick={confirmDelete} color="error" variant="contained">Delete</Button></DialogActions>
      </Dialog>

      {/* Modify Project Dialog */}
      <Dialog open={openModifyDialog} onClose={handleModifyDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Modify Project</DialogTitle>
        <DialogContent dividers>
          {currentProjectToModify && (
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField fullWidth label="Project Name" name="name" value={currentProjectToModify.name} onChange={handleProjectChange} margin="normal" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Location" name="location" value={currentProjectToModify.location} onChange={handleProjectChange} margin="normal" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Created By" name="createdBy" value={currentProjectToModify.createdBy} onChange={handleProjectChange} margin="normal" /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={handleModifyDialogClose}>Cancel</Button><Button onClick={handleModifyDialogSave} variant="contained">Save Changes</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectsList;